export const CATEGORIES = [
  "Rouge",
  "Blanc",
  "Effervescent",
  "Liquoreux",
] as const;

/**
 * Anciennes valeurs conservées uniquement pour relire et reclasser sans perte
 * d'éventuelles fiches créées avant la simplification des catégories.
 */
export const LEGACY_CATEGORIES = [
  "Rosé",
  "Orange",
  "Vin fortifié / vin doux naturel",
  "Autre",
] as const;

export type CurrentWineCategory = (typeof CATEGORIES)[number];
export type LegacyWineCategory = (typeof LEGACY_CATEGORIES)[number];
export type WineCategory = CurrentWineCategory | LegacyWineCategory;

export type CellarPlace = string;

export interface CellarPlaceRecord {
  id: string;
  nom: string;
  date_modification?: string;
  nombre_emplacements: number;
  nombre_fiches: number;
  stock: number;
}

export interface CellarLocation {
  id: string;
  lieu_id: string | null;
  lieu: CellarPlace | null;
  nom: string;
  libelle: string;
  capacite_totale: number | null;
  date_modification?: string;
  nombre_fiches: number;
  stock: number;
}

export interface LocationFields {
  lieu_id: string;
  nom: string;
  capacite_totale: number | null;
}

export const MOVEMENT_REASONS = [
  "Stock initial",
  "Achat",
  "Bouteille bue",
  "Correction",
  "Transfert",
] as const;

export type MovementReason = (typeof MOVEMENT_REASONS)[number];

export type TastingStatus =
  | "Non renseigné"
  | "À garder"
  | "À boire dès maintenant";

export interface WineLocationStock {
  emplacement_id: string | null;
  lieu_id: string | null;
  lieu: CellarPlace | null;
  emplacement: string | null;
  emplacement_nom: string | null;
  stock: number;
}

export interface Movement {
  id: string;
  vin_id: string;
  quantite: number;
  motif: MovementReason;
  date_effective: string | null;
  date_creation: string;
  date_modification?: string | null;
  mouvement_annule_id?: string | null;
  prix_unitaire: number | null;
  vendeur: string | null;
  note: string | null;
  /** Présents sur les réponses récentes ; facultatifs pour les anciennes copies hors ligne. */
  emplacement_id?: string | null;
  emplacement?: string | null;
  lieu_id?: string | null;
  lieu?: CellarPlace | null;
  transfert_id?: string | null;
  coravin_id?: string | null;
}

/** Une bouteille suivie à partir de son premier prélèvement au Coravin. */
export interface CoravinBottle {
  id: string;
  vin_id: string;
  emplacement_id: string | null;
  emplacement: string | null;
  lieu_id: string | null;
  lieu: string | null;
  date_coravin: string;
  date_fin: string | null;
  note: string | null;
  mouvement_fin_id: string | null;
  date_creation: string;
  date_modification: string;
}

export interface CreateCoravinPayload {
  emplacement_id: string | null;
  date_effective: string;
  note?: string | null;
}

export interface FinishCoravinPayload {
  date_effective: string;
  note?: string | null;
}

export interface Wine {
  id: string;
  producteur: string;
  nom: string;
  appellation: string | null;
  region: string | null;
  cepages: string | null;
  vinification_elevage: string | null;
  millesime: number | null;
  categorie: WineCategory;
  format: string;
  emplacement_id: string | null;
  emplacement: string | null;
  lieu_id: string | null;
  lieu: CellarPlace | null;
  debut_deg: number | null;
  fin_deg: number | null;
  note: string | null;
  date_creation?: string;
  date_modification?: string;
  stock: number;
  /** Ventilation du stock. Absente uniquement dans d'anciennes copies hors ligne. */
  stocks_par_emplacement?: WineLocationStock[];
  a_une_note_degustation?: boolean;
  dernier_prix_achat?: number | null;
  mouvements?: Movement[];
  /** Absente uniquement dans les anciennes copies hors ligne. */
  coravins?: CoravinBottle[];
}

export interface WineFields {
  producteur: string;
  nom: string;
  appellation: string;
  region: string;
  cepages: string;
  vinification_elevage: string;
  millesime: number | null;
  categorie: WineCategory;
  format: string;
  emplacement_id: string | null;
  debut_deg: number | null;
  fin_deg: number | null;
  note: string;
}

export interface EntryFields {
  quantite: number;
  date_effective: string;
  prix_unitaire: number | null;
  vendeur: string;
  note: string;
}

export interface CreateWinePayload extends WineFields {
  entree: EntryFields;
  repartition_initiale?: InitialStockAllocation[];
}

export interface InitialStockAllocation {
  emplacement_id: string | null;
  quantite: number;
}

export interface CreateMovementPayload {
  quantite: number;
  motif: MovementReason;
  date_effective?: string | null;
  prix_unitaire?: number | null;
  vendeur?: string | null;
  note?: string | null;
  stock_reel?: number;
  emplacement_id?: string | null;
  repartition?: InitialStockAllocation[];
}

export interface TransferWinePayload {
  source_emplacement_id: string | null;
  destination_emplacement_id: string | null;
  quantite: number;
  date_effective?: string | null;
  note?: string | null;
  /** Identifie les bouteilles coravinées incluses dans le déplacement. */
  coravin_ids?: string[];
}

export interface TransferWineResult {
  mouvements: Movement[];
  stock: number;
  stocks_par_emplacement?: WineLocationStock[];
}

export interface UpdateMovementPayload {
  date_effective: string | null;
  note: string | null;
  prix_unitaire?: number | null;
  vendeur?: string | null;
}

export interface CancelMovementPayload {
  date_effective: string | null;
  note?: string | null;
}

export type CompletionField =
  | "appellation"
  | "region"
  | "cepages"
  | "periode"
  | "prix"
  | "vinification_elevage";
